import { html } from "lit";

import type {
  ObjetTouristique,
  Capacites,
  PeriodeOuverture,
  PeriodeTarif,
  Tarif,
} from "../../types/apidae.d";
import type { FermetureExceptionnelle } from "../../types/apidae.d";
import {
  getPrestationIconName,
  type PrestationType,
} from "../../config/prestationIcons";
import { getAnimalWording } from "../../config/animalWording";
import type { WidgetConfiguration } from "../../types/widgets.d";
import {
  DisplayedFieldId,
  WidgetFieldsInfos,
  WidgetLanguage,
} from "../../types/widgets.d";
import {
  getDisplayedFieldData,
  getLocationDisplayData,
} from "../../utils/displayedFieldUtils";
import { TranslationService } from "../../services/TranslationService";
import {
  buildWeeklyScheduleFromHoraires,
  filterFermeturesForPeriod,
  formatFermeturesExceptionnelles,
  hasFermetureDisplayDate,
  formatOuverturePeriodLabel,
  getPeriodDurationInDays,
} from "../../utils/openingUtils";
import {
  formatPricingPeriodLabel,
  formatTarifPrice,
} from "../../utils/pricingUtils";
import { getTarifTypeIconName } from "../../config/tarifTypeIcons";
import { getModePaiementIconName } from "../../config/modePaiementIcons";
import {
  DESCRIPTIF_THEME_IDS_BY_SECTION,
  getDescriptifThemeIconName,
  getDescriptifThemeVariant,
  isOtherDescriptifTheme,
  isTopoPasAPasTheme,
} from "../../config/descriptifThemeIcons";
import { getTypeClienteleIconName } from "../../config/typeClienteleIcons";
import { t } from "../../utils/widgetsUtils";
import "./OpenSystemWidget";
import "./EllohaWidget";

type ListingItem = {
  icon: string;
  title: string;
  content: string;
  url?: string;
};

type DetailViewContext = {
  item: ObjetTouristique;
  language: WidgetLanguage;
  displayedFields?: WidgetFieldsInfos[];
  shouldDisplayField(fieldId: string): boolean;
  getTelephones(): any[];
  getEmails(): any[];
  getWebsites(): any[];
  getReservationUrl(): string | null;
  getLabel(libelle: any): string;
  getCoordonneesValue(coordonnees: any): string | null;
  getMultimediaIcon(type: string): string;
  formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string;
  formatOuverturePeriod(periode: PeriodeOuverture): string | null;
  getPeriodesGroupedByYear(): Map<number, PeriodeOuverture[]>;
  getOuvertureItems(
    periode: PeriodeOuverture,
  ): Array<{ icon: string; title: string; content: string }>;
  currentOpeningPeriodIndex: number;
  setCurrentOpeningPeriodIndex(index: number): void;
  currentPricingPeriodIndex: number;
  setCurrentPricingPeriodIndex(index: number): void;
  /** Configuration du widget parent (pour la carte) */
  widgetConfig?: WidgetConfiguration | null;
  /** Fait défiler jusqu'à la section carte */
  scrollToMapSection?(): void;
  /** Carte chargée (chargement à la demande pour éviter dépendance circulaire) */
  mapWidgetLoaded?: boolean;
};

/** Normalise les retours à la ligne de l'API (\r\n, \r) en \n pour affichage avec white-space: pre-line. */
function normalizeNewlines(s: string): string {
  return s.replace(/\r\n|\r/g, "\n");
}

function normalizeNewlinesInObject(obj: unknown): unknown {
  if (typeof obj === "string") return normalizeNewlines(obj);
  if (Array.isArray(obj)) return obj.map(normalizeNewlinesInObject);
  if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, normalizeNewlinesInObject(v)]),
    );
  }
  return obj;
}

export function getFieldData(
  ctx: DetailViewContext,
  fieldId: DisplayedFieldId,
) {
  const fieldConfig = ctx.displayedFields?.find(
    (field) => field.id === fieldId,
  );

  const result = getDisplayedFieldData(ctx.item, fieldId, {
    language: ctx.language,
    getLabel: (libelle) => ctx.getLabel(libelle),
    formatDate:
      "formatDate" in ctx && typeof ctx.formatDate === "function"
        ? (dateStr: string, options?: Intl.DateTimeFormatOptions) =>
            ctx.formatDate(dateStr, options)
        : (dateStr: string) => dateStr,
    t: (key: string) => t(key, ctx.language),
    fieldConfig,
  });

  return normalizeNewlinesInObject(result) as typeof result;
}

/**
 * Section Localisation (gauche, sans border) + Contact (droite).
 * Localisation : icône map_point, nomDuLieu, adresse1/2/3, codePostal commune cedex, bureauDistribution, pays, bouton "Voir sur la carte".
 * Le champ localisation est toujours récupéré via l'API (baseFields dans WidgetService).
 * Contact : moyens non-réseaux sociaux en liste verticale, séparateur, réseaux sociaux en icônes horizontales, puis bouton Réserver si URL site web.
 */
export function renderLocationAndContactSection(ctx: DetailViewContext) {
  const location = getLocationDisplayData(ctx.item, {
    getLabel: (l) => ctx.getLabel(l),
  });
  const showLocation = !!location;

  const contactData = getFieldData(ctx, DisplayedFieldId.MOYENS_COMMUNICATION);
  const hasContact =
    ctx.shouldDisplayField(DisplayedFieldId.MOYENS_COMMUNICATION) &&
    contactData?.items &&
    (contactData.items as any[]).length > 0;

  const items = (contactData?.items || []) as Array<{
    icon: string;
    title: string;
    content: string;
    url?: string;
    isSocial: boolean;
  }>;
  const nonSocialItems = items.filter((i) => !i.isSocial);
  const socialItems = items.filter((i) => i.isSocial);
  const reservationUrl =
    (contactData as { reservationUrl?: string } | null)?.reservationUrl ?? null;

  const showContact =
    hasContact &&
    (nonSocialItems.length > 0 ||
      socialItems.length > 0 ||
      (reservationUrl && reservationUrl.length > 0));

  if (!showLocation && !showContact) {
    return html``;
  }

  const listingItems: ListingItem[] = nonSocialItems.map((i) => ({
    icon: i.icon,
    title: "" /* pas de libellé, seulement icône + contenu */,
    content: i.content,
    url: i.url,
  }));

  const lineCodePostalCommuneCedex = [
    location?.codePostal,
    location?.communeNom,
    location?.cedex,
  ]
    .filter(Boolean)
    .join(" ");

  return html`
    <div class="detail-content-row detail-content-row--location-contact">
      <div class="detail-content-row-item">
        ${showLocation
          ? html`
              <div class="detail-location-block">
                <div class="detail-location-block__main">
                  <apidae-icon
                    name="bold/map_&_location/map_point"
                    size="16"
                    color="currentColor"
                    class="detail-location-block__icon"
                  ></apidae-icon>
                  <div class="detail-location-block__lines">
                    ${location.nomDuLieu
                      ? html`<div class="detail-location-block__line">
                          ${location.nomDuLieu}
                        </div>`
                      : ""}
                    ${location.adresse1
                      ? html`<div class="detail-location-block__line">
                          ${location.adresse1}
                        </div>`
                      : ""}
                    ${location.adresse2
                      ? html`<div class="detail-location-block__line">
                          ${location.adresse2}
                        </div>`
                      : ""}
                    ${location.adresse3
                      ? html`<div class="detail-location-block__line">
                          ${location.adresse3}
                        </div>`
                      : ""}
                    ${lineCodePostalCommuneCedex
                      ? html`<div class="detail-location-block__line">
                          ${lineCodePostalCommuneCedex}
                        </div>`
                      : ""}
                    ${location.bureauDistribution
                      ? html`<div class="detail-location-block__line">
                          ${location.bureauDistribution}
                        </div>`
                      : ""}
                    ${location.paysLabel
                      ? html`<div class="detail-location-block__line">
                          ${location.paysLabel}
                        </div>`
                      : ""}
                  </div>
                </div>
                <apidae-button
                  variant="secondary100"
                  class="detail-location-block__map-button"
                  aria-label="${t("viewOnMap", ctx.language)}"
                  @click=${() => ctx.scrollToMapSection?.()}
                >
                  ${t("viewOnMap", ctx.language)}
                  <apidae-icon
                    name="bold/arrows/round_arrow_right"
                    size="22"
                    color="currentColor"
                    slot="icon"
                  ></apidae-icon>
                </apidae-button>
              </div>
            `
          : ""}
      </div>
      <div class="detail-content-row-item">
        ${showContact
          ? html`
              <apidae-detail-view-information-block variant="secondary100">
                <div slot="header">${t("contact", ctx.language)}</div>
                <div slot="content" class="contact-information-content">
                  ${nonSocialItems.length > 0
                    ? html`
                        <apidae-detail-view-information-block-listing
                          class="contact-listing"
                          .items=${listingItems}
                        ></apidae-detail-view-information-block-listing>
                      `
                    : ""}
                  ${nonSocialItems.length > 0 && socialItems.length > 0
                    ? html`<div class="contact-separator"></div>`
                    : ""}
                  ${socialItems.length > 0
                    ? html`
                        <div class="contact-social-row">
                          ${socialItems.map(
                            (s) => html`
                              <a
                                class="contact-social-link"
                                href="${s.url || s.content || "#"}"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="${s.title}"
                                aria-label="${s.title}"
                              >
                                <apidae-icon
                                  name="${s.icon}"
                                  size="28"
                                  preserve-colors
                                ></apidae-icon>
                              </a>
                            `,
                          )}
                        </div>
                      `
                    : ""}
                  ${reservationUrl
                    ? html`
                        <a
                          href="${reservationUrl}"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="contact-reserve-button"
                        >
                          ${t("reserve", ctx.language)}
                        </a>
                      `
                    : ""}
                </div>
              </apidae-detail-view-information-block>
            `
          : ""}
      </div>
    </div>
  `;
}

function renderOpeningManualBlock(
  ctx: DetailViewContext,
  periodeEnClair: string,
) {
  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          variant="secondary100"
          ?spacer=${false}
        >
          <div slot="header">
            <apidae-icon
              name="bold/time/clock_circle"
              size="20"
              color="currentColor"
              inner-fill="var(--widget-secondary-color, #ffffff)"
            ></apidae-icon>
            ${t("openings", ctx.language)}
          </div>
          <div slot="content">
            <p>${periodeEnClair}</p>
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

function renderOnePeriodBlock(
  ctx: DetailViewContext,
  periode: PeriodeOuverture,
  fermetures: FermetureExceptionnelle[],
) {
  const periodLabel = formatOuverturePeriodLabel(
    periode,
    (d, o) => ctx.formatDate(d, o),
    {
      periodFrom: t("periodFrom", ctx.language),
      periodTo: t("periodTo", ctx.language),
    },
    periode.tousLesAns === true,
  );
  const locale = TranslationService.getLocale(ctx.language);
  const schedule = buildWeeklyScheduleFromHoraires(periode.horaires, {
    locale,
    closedLabel: t("closed", ctx.language),
    openLabel: t("open", ctx.language),
    noDataLabel: "/",
  });
  const fermeturesForPeriod = filterFermeturesForPeriod(
    fermetures.filter(hasFermetureDisplayDate),
    periode,
  );
  // Formate le jour avec ordinal (ex. 1 → "1er", 15 → "15th"). Les clés dayOrdinal1/2/3 et dayOrdinalOther ne contiennent que le suffixe (sans le chiffre).
  const getOrdinalDay = (day: number): string => {
    const suffix1 = t("dayOrdinal1", ctx.language);
    const suffix2 = t("dayOrdinal2", ctx.language);
    const suffix3 = t("dayOrdinal3", ctx.language);
    const suffixOther = t("dayOrdinalOther", ctx.language);

    // Jours 1, 2, 3 : chiffre + suffixe dédié (ex. "1" + "er" → "1er")
    if (day === 1) return suffix1 ? `1${suffix1}` : "1";
    if (day === 2) return suffix2 ? `2${suffix2}` : "2";
    if (day === 3) return suffix3 ? `3${suffix3}` : "3";

    // Jours 4 à 31 : uniquement si la langue définit un suffixe "autre" (ex. EN "th" pour 4th, 5th…).
    // Sinon on renvoie "" : formatDateWithOrdinal (openingUtils) utilisera alors formatDate(…, { day: "numeric", month: "long" }) et affichera par ex. "15 août".
    if (!suffixOther || day < 4 || day > 31) return "";

    // Choisir le bon suffixe selon le dernier chiffre du jour :
    // - 11, 12, 13 → toujours suffixOther (ex. 11th, 12th, 13th en anglais)
    // - 21, 22, 23, 31 → même suffixe que 1, 2, 3 (ex. 21st, 22nd, 23rd, 31st)
    // - le reste → suffixOther (ex. 4th, 5th… 20th, 24th… 30th)
    const lastDigit = day % 10;
    let suffix: string;
    if (day >= 11 && day <= 13) {
      suffix = suffixOther;
    } else if (lastDigit === 1) {
      suffix = suffix1 || suffixOther;
    } else if (lastDigit === 2) {
      suffix = suffix2 || suffixOther;
    } else if (lastDigit === 3) {
      suffix = suffix3 || suffixOther;
    } else {
      suffix = suffixOther;
    }
    return `${day}${suffix}`;
  };
  const disclaimerText =
    fermeturesForPeriod.length > 0
      ? formatFermeturesExceptionnelles(
          fermeturesForPeriod,
          (d, o) => ctx.formatDate(d, o),
          t("exceptionalClosurePrefix", ctx.language) ?? "",
          getOrdinalDay,
          {
            getDateSpecialeLabel: (key) =>
              t(`dateSpeciale_${key}`, ctx.language) || key,
          },
        )
      : null;

  return html`
    <div class="opening-period-block">
      ${periode.nom
        ? html`<p class="opening-period-name">${periode.nom}</p>`
        : ""}
      ${periodLabel
        ? html`<p class="opening-period-dates">${periodLabel}</p>`
        : ""}
      <div class="information-block-spacer" aria-hidden="true"></div>
      <div class="opening-weekly-schedule">
        ${schedule.map(
          (row) => html`
            <div class="opening-weekly-schedule-row">
              <span class="opening-weekly-day">${row.dayName}</span>
              <span class="opening-weekly-times">${row.text}</span>
            </div>
          `,
        )}
      </div>
      ${disclaimerText
        ? html`
            <apidae-detail-view-information-block-disclaimer
              color="primary10"
              logo="bold/essentional_ui/danger_triangle"
              .text=${disclaimerText}
            ></apidae-detail-view-information-block-disclaimer>
          `
        : ""}
    </div>
  `;
}

export function renderOpeningSection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.OUVERTURE)) {
    return html``;
  }
  const data = getFieldData(ctx, DisplayedFieldId.OUVERTURE);
  if (!data) return html``;

  const periodes = data.openingPeriodes ?? [];
  const mode = data.openingMode ?? "MANUEL";
  const periodeEnClair = data.openingPeriodeEnClair ?? "";
  const fermetures = data.openingFermeturesExceptionnelles ?? [];

  const showAuto = periodes.length > 0 && mode === "AUTOMATIQUE";

  if (!showAuto) {
    if (!periodeEnClair.trim()) return html``;
    return renderOpeningManualBlock(ctx, periodeEnClair);
  }

  // Si toutes les périodes font < 7 j : afficher periodeEnClair au lieu du tableau (évite des tableaux avec beaucoup de "/"). Dès qu’une période fait >= 7 j : garder le tableau.
  const hasAnyLongPeriod = periodes.some(
    (p) => getPeriodDurationInDays(p) >= 7,
  );
  const allPeriodsShort =
    periodes.length > 0 &&
    periodes.every((p) => getPeriodDurationInDays(p) < 7) &&
    periodeEnClair.trim();
  if (allPeriodsShort && !hasAnyLongPeriod) {
    return renderOpeningManualBlock(ctx, periodeEnClair);
  }

  const CAROUSEL_STEP = 2;
  const idx = ctx.currentOpeningPeriodIndex;
  const maxIdx = Math.max(0, periodes.length - 1);
  const currentIdx = Math.min(idx, maxIdx);
  const hasMultiple = periodes.length > 1;
  const leftPeriode = periodes[currentIdx];
  const rightPeriode =
    hasMultiple && currentIdx + 1 <= maxIdx ? periodes[currentIdx + 1] : null;

  const goPrev = () =>
    ctx.setCurrentOpeningPeriodIndex(Math.max(0, currentIdx - CAROUSEL_STEP));
  const goNext = () =>
    ctx.setCurrentOpeningPeriodIndex(
      Math.min(maxIdx, currentIdx + CAROUSEL_STEP),
    );
  const canGoPrev = hasMultiple && currentIdx >= CAROUSEL_STEP;
  const canGoNext = hasMultiple && currentIdx + CAROUSEL_STEP <= maxIdx;

  return html`
    <div class="detail-content-row">
      <div
        class="detail-content-row-item ${!hasMultiple
          ? "opening-single-period"
          : ""}"
      >
        <apidae-detail-view-information-block
          variant="secondary100"
          ?spacer=${false}
        >
          <div slot="header" class="opening-section-title">
            ${t("openings", ctx.language)}
          </div>
          <div
            slot="content"
            class="opening-carousel-slot ${!hasMultiple
              ? "opening-carousel-slot--single"
              : ""}"
          >
            ${hasMultiple
              ? html`
                  <button
                    class="opening-carousel-nav opening-carousel-nav-prev ${!canGoPrev
                      ? "inactive"
                      : ""}"
                    @click=${goPrev}
                    aria-label=${t("previous", ctx.language) ?? "Précédent"}
                    ?disabled=${!canGoPrev}
                  >
                    <apidae-icon
                      name="bold/arrows/round_alt_arrow_left"
                      size="24"
                      color="currentColor"
                    ></apidae-icon>
                  </button>
                `
              : ""}
            <div class="opening-carousel-tables">
              <div class="opening-carousel-table opening-carousel-table-left">
                ${leftPeriode
                  ? renderOnePeriodBlock(ctx, leftPeriode, fermetures)
                  : ""}
              </div>
              <div class="opening-carousel-table opening-carousel-table-right">
                ${rightPeriode
                  ? renderOnePeriodBlock(ctx, rightPeriode, fermetures)
                  : ""}
              </div>
            </div>
            ${hasMultiple
              ? html`
                  <button
                    class="opening-carousel-nav opening-carousel-nav-next ${!canGoNext
                      ? "inactive"
                      : ""}"
                    @click=${goNext}
                    aria-label=${t("next", ctx.language) ?? "Suivant"}
                    ?disabled=${!canGoNext}
                  >
                    <apidae-icon
                      name="bold/arrows/round_alt_arrow_right"
                      size="24"
                      color="currentColor"
                    ></apidae-icon>
                  </button>
                `
              : ""}
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

export function renderLongDescriptionSection(ctx: DetailViewContext) {
  const descriptifCourtData = getFieldData(
    ctx,
    DisplayedFieldId.DESCRIPTIF_COURT,
  );
  const descriptifDetailleData = getFieldData(
    ctx,
    DisplayedFieldId.DESCRIPTIF_DETAILLE,
  );

  const showCourt =
    ctx.shouldDisplayField(DisplayedFieldId.DESCRIPTIF_COURT) &&
    !!descriptifCourtData?.value;
  const showDetaille =
    ctx.shouldDisplayField(DisplayedFieldId.DESCRIPTIF_DETAILLE) &&
    !!descriptifDetailleData?.value;

  if (!showCourt && !showDetaille) return html``;

  if (!showCourt && !showDetaille) return html``;

  let longDescriptionContent: ReturnType<typeof html> = html``;
  if (showDetaille && descriptifDetailleData?.value) {
    const fullDescription = descriptifDetailleData.value;

    // Règle métier : texte ≤ 400 → tout afficher ; texte > 400 → couper en fin de mot. Ne proposer "Voir tout" que si au moins 150 caractères supplémentaires seraient affichés au clic.
    const CROP_THRESHOLD = 400;
    const CROP_AT = 250;
    const MIN_ADDITIONAL_CHARS = 150;

    const truncateAtWord = (text: string, maxLen: number): string => {
      if (text.length <= maxLen) return text;
      const slice = text.slice(0, maxLen);
      const lastSpace = slice.lastIndexOf(" ");
      const cutIndex = lastSpace >= 0 ? lastSpace : maxLen;
      return slice.slice(0, cutIndex).trimEnd() + "…";
    };

    const isString = typeof fullDescription === "string";
    const truncatedForCheck =
      isString && fullDescription.length > CROP_THRESHOLD
        ? truncateAtWord(fullDescription, CROP_AT)
        : (fullDescription as string);
    const additionalCharsWhenExpanded = isString
      ? fullDescription.length -
        (truncatedForCheck.length - 1) /* sans le "…" */
      : 0;
    const hasLongDescription =
      isString &&
      fullDescription.length > CROP_THRESHOLD &&
      additionalCharsWhenExpanded >= MIN_ADDITIONAL_CHARS;
    const truncatedDescription = hasLongDescription
      ? truncatedForCheck
      : fullDescription;

    const seeAllLabel = t("seeAll", ctx.language) ?? "Voir tout";
    const showLessLabel = t("showLess", ctx.language) ?? "Voir moins";

    longDescriptionContent = html`
      <p>${truncatedDescription}</p>
      ${hasLongDescription
        ? html`
            <apidae-button
              variant="secondary100"
              class="apidae-detail-view-see-more-button"
              @click=${(e: Event) => {
                const button = e.currentTarget as HTMLElement;
                const paragraph =
                  button.previousElementSibling as HTMLParagraphElement | null;

                if (!paragraph) return;

                const isExpanded =
                  button.getAttribute("data-expanded") === "true";

                if (isExpanded) {
                  paragraph.textContent = truncatedDescription as string;
                  button.textContent = seeAllLabel;
                  button.setAttribute("data-expanded", "false");
                } else {
                  paragraph.textContent = fullDescription as string;
                  button.textContent = showLessLabel;
                  button.setAttribute("data-expanded", "true");
                }
              }}
              style="margin-top: 4px;"
            >
              ${seeAllLabel}
            </apidae-button>
          `
        : ""}
    `;
  }

  const singlePart = showCourt !== showDetaille;

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          variant="grey"
          class="detail-view-descriptif-block${singlePart
            ? " detail-view-descriptif-block--single-part"
            : ""}"
        >
          <div slot="header">
            ${showCourt ? html`<p>${descriptifCourtData!.value}</p>` : ""}
          </div>
          <div slot="content">${longDescriptionContent}</div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

/** Bloc manuel : icône Euro, titre Tarifs, texte tarifsEnClair (sans moyens de paiement) */
function renderPricingManualBlock(
  ctx: DetailViewContext,
  tarifsEnClair: string,
) {
  const pricingTitle = t("pricing", ctx.language) ?? "Tarifs";
  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          variant="secondary100"
          ?spacer=${false}
        >
          <div slot="header" class="pricing-section-title">
            <apidae-icon
              name="bold/money/euro"
              size="20"
              color="currentColor"
            ></apidae-icon>
            ${pricingTitle}
          </div>
          <div slot="content">
            <p>${tarifsEnClair}</p>
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

/** Une période tarifaire : "Du ... au ...", séparateur, liste tarifs avec icône + libellé + prix */
function renderOnePricingPeriodBlock(
  ctx: DetailViewContext,
  periode: PeriodeTarif,
) {
  const periodLabel = formatPricingPeriodLabel(
    periode,
    (dateStr, options) => ctx.formatDate(dateStr, options),
    {
      periodFrom: t("periodFrom", ctx.language) ?? "Du",
      periodTo: t("periodTo", ctx.language) ?? "au",
    },
  );
  const tarifs = periode.tarifs ?? [];

  return html`
    <div class="pricing-period-block">
      ${periodLabel
        ? html`<p class="pricing-period-dates">${periodLabel}</p>`
        : ""}
      <div class="information-block-spacer" aria-hidden="true"></div>
      <div class="pricing-tarifs-list">
        ${tarifs.map((tarif: Tarif) => {
          const typeLabel =
            ctx.getLabel(tarif.type) || tarif.type?.libelleFr || "";
          const priceText = formatTarifPrice(tarif, (libelle) =>
            ctx.getLabel(libelle),
          );
          const iconName = getTarifTypeIconName(tarif.type);
          return html`
            <div class="pricing-tarif-row">
              <span class="pricing-tarif-label">
                <apidae-icon
                  name="${iconName}"
                  size="20"
                  color="currentColor"
                ></apidae-icon>
                <span>${typeLabel}</span>
              </span>
              ${priceText
                ? html`<span class="pricing-tarif-price">${priceText}</span>`
                : ""}
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

export function renderPricingSection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.TARIFS)) {
    return html``;
  }
  const data = getFieldData(ctx, DisplayedFieldId.TARIFS);
  if (!data) return html``;

  const periodes = data.pricingPeriodes ?? [];
  const mode = data.pricingMode ?? "MANUEL";
  const tarifsEnClair =
    data.pricingTarifsEnClair ?? (data.value as string | undefined) ?? "";

  const showAuto = periodes.length > 0 && mode === "AUTOMATIQUE";

  if (!showAuto) {
    if (!tarifsEnClair.trim()) return html``;
    return renderPricingManualBlock(ctx, tarifsEnClair);
  }

  const CAROUSEL_STEP = 2;
  const idx = ctx.currentPricingPeriodIndex;
  const maxIdx = Math.max(0, periodes.length - 1);
  const currentIdx = Math.min(idx, maxIdx);
  const hasMultiple = periodes.length > 1;
  const leftPeriode = periodes[currentIdx];
  const rightPeriode =
    hasMultiple && currentIdx + 1 <= maxIdx ? periodes[currentIdx + 1] : null;

  const goPrev = () =>
    ctx.setCurrentPricingPeriodIndex(Math.max(0, currentIdx - CAROUSEL_STEP));
  const goNext = () =>
    ctx.setCurrentPricingPeriodIndex(
      Math.min(maxIdx, currentIdx + CAROUSEL_STEP),
    );
  const canGoPrev = hasMultiple && currentIdx >= CAROUSEL_STEP;
  const canGoNext = hasMultiple && currentIdx + CAROUSEL_STEP <= maxIdx;

  const pricingTitle = t("pricing", ctx.language) ?? "Tarifs";

  return html`
    <div class="detail-content-row">
      <div
        class="detail-content-row-item ${!hasMultiple
          ? "pricing-single-period"
          : ""}"
      >
        <apidae-detail-view-information-block
          variant="secondary100"
          ?spacer=${false}
        >
          <div slot="header" class="pricing-section-title">${pricingTitle}</div>
          <div
            slot="content"
            class="pricing-carousel-slot ${!hasMultiple
              ? "pricing-carousel-slot--single"
              : ""}"
          >
            ${hasMultiple
              ? html`
                  <button
                    class="pricing-carousel-nav pricing-carousel-nav-prev ${!canGoPrev
                      ? "inactive"
                      : ""}"
                    @click=${goPrev}
                    aria-label=${t("previous", ctx.language) ?? "Précédent"}
                    ?disabled=${!canGoPrev}
                  >
                    <apidae-icon
                      name="bold/arrows/round_alt_arrow_left"
                      size="24"
                      color="currentColor"
                    ></apidae-icon>
                  </button>
                `
              : ""}
            <div class="pricing-carousel-tables">
              <div class="pricing-carousel-table pricing-carousel-table-left">
                ${leftPeriode
                  ? renderOnePricingPeriodBlock(ctx, leftPeriode)
                  : ""}
              </div>
              <div class="pricing-carousel-table pricing-carousel-table-right">
                ${rightPeriode
                  ? renderOnePricingPeriodBlock(ctx, rightPeriode)
                  : ""}
              </div>
            </div>
            ${hasMultiple
              ? html`
                  <button
                    class="pricing-carousel-nav pricing-carousel-nav-next ${!canGoNext
                      ? "inactive"
                      : ""}"
                    @click=${goNext}
                    aria-label=${t("next", ctx.language) ?? "Suivant"}
                    ?disabled=${!canGoNext}
                  >
                    <apidae-icon
                      name="bold/arrows/round_alt_arrow_right"
                      size="24"
                      color="currentColor"
                    ></apidae-icon>
                  </button>
                `
              : ""}
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

export function renderReservationSection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.INFORMATIONS_RESERVATION)) {
    return html``;
  }
  const reservationData = getFieldData(
    ctx,
    DisplayedFieldId.INFORMATIONS_RESERVATION,
  );
  const complementText = reservationData?.value;

  if (!complementText?.trim()) {
    return html``;
  }

  const reservationTitle = t("reservation", ctx.language) ?? "Réservations";

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block variant="grey">
          <div slot="header">
            <apidae-icon
              name="bold/arrows/square_arrow_left_up"
              size="20"
              color="currentColor"
            ></apidae-icon>
            ${reservationTitle}
          </div>
          <div slot="content">
            <p>${complementText}</p>
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

/** Extensions des plans considérés comme tracés (KML, KMZ, GPX) à afficher dans MapWidget */
const PLAN_TRACK_EXTENSIONS = ["kml", "kmz", "gpx"];

export function renderExtraDocumentationSection(ctx: DetailViewContext) {
  if (
    !ctx.shouldDisplayField(DisplayedFieldId.MULTIMEDIAS) ||
    !getFieldData(ctx, DisplayedFieldId.MULTIMEDIAS)
  ) {
    return html``;
  }

  const multimediasData = getFieldData(ctx, DisplayedFieldId.MULTIMEDIAS);
  const docs = multimediasData?.items || [];

  if (docs.length === 0) {
    return html``;
  }

  const planTracks = docs.filter(
    (ill: { type: string; extension?: string; url?: string | null }) =>
      ill.type === "PLAN" &&
      ill.url &&
      ill.extension &&
      PLAN_TRACK_EXTENSIONS.includes(ill.extension),
  );
  const firstPlanTrack = planTracks[0] as { url: string } | undefined;

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block variant="secondary100">
          <div slot="header">
            ${t("extraDocumentation", ctx.language) ??
            "Documentation supplémentaire"}
          </div>
          <div slot="content">
            <div style="display: flex; flex-direction: column; gap: 16px;">
              ${docs.map(
                (ill: { type: string; url?: string | null; nom?: string }) => {
                  const iconName = ctx.getMultimediaIcon(ill.type);

                  return html`
                    <a
                      href="${ill.url ?? ""}"
                      target="_blank"
                      rel="noopener noreferrer"
                      style="
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      text-decoration: underline;
                      color: inherit;
                    "
                    >
                      <apidae-icon
                        name="${iconName}"
                        size="20"
                        color="currentColor"
                      ></apidae-icon>
                      <span>${ill.nom}</span>
                    </a>
                  `;
                },
              )}
            </div>
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
    ${firstPlanTrack && ctx.mapWidgetLoaded
      ? html`
          <div class="detail-track-map-section">
            <apidae-widget-carte
              ischild
              .trackUrl=${firstPlanTrack.url}
              .items=${[]}
              .config=${ctx.widgetConfig ?? null}
              .hideHeaderAndFilters=${true}
              .hideEmptyState=${true}
              .isChildWidget=${true}
            ></apidae-widget-carte>
          </div>
        `
      : firstPlanTrack && !ctx.mapWidgetLoaded
        ? html`<div
            class="detail-track-map-section detail-map-loading"
            aria-busy="true"
          ></div>`
        : html``}
  `;
}

export function renderEquipmentsSection(ctx: DetailViewContext) {
  if (
    !ctx.shouldDisplayField(DisplayedFieldId.EQUIPEMENTS) ||
    !getFieldData(ctx, DisplayedFieldId.EQUIPEMENTS)
  ) {
    return html``;
  }

  const equipementsData = getFieldData(ctx, DisplayedFieldId.EQUIPEMENTS);
  const equipementsItems = (equipementsData?.items || []) as Array<{
    type: string;
    id: number;
    enumKey?: string;
    label: string;
  }>;

  if (equipementsItems.length === 0) {
    return html``;
  }

  const allEquipments = equipementsItems.map((eq) => ({
    icon: getPrestationIconName(eq.type as PrestationType, {
      id: eq.id,
      enumKey: eq.enumKey,
    }),
    title: eq.label,
    content: "",
  }));

  const maxVisible = 12;
  const hasMoreThanMax = allEquipments.length > maxVisible;
  const visibleEquipments = hasMoreThanMax
    ? allEquipments.slice(0, maxVisible)
    : allEquipments;
  const seeAllLabel = `${t("seeAll", ctx.language) ?? "Voir tout"} (${allEquipments.length})`;
  const seeLessLabel = t("showLess", ctx.language) ?? "Voir moins";

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          variant="secondary100"
          spacer="true"
          class="information-block--content-500"
        >
          <div slot="header">Equipements</div>
          <div slot="content">
            <apidae-detail-view-information-block-listing-w-columns
              class="equipments-listing"
              data-all-items="${JSON.stringify(allEquipments)}"
              data-visible-items="${JSON.stringify(visibleEquipments)}"
              .items=${visibleEquipments}
            ></apidae-detail-view-information-block-listing-w-columns>
            ${hasMoreThanMax
              ? html`
                  <apidae-button
                    variant="secondary100"
                    @click=${(e: Event) => {
                      const button = e.currentTarget as HTMLElement;
                      const listing = button.previousElementSibling as any;

                      if (!listing) return;

                      const isExpanded =
                        listing.getAttribute("data-expanded") === "true";
                      const allItems = JSON.parse(
                        listing.getAttribute("data-all-items") || "[]",
                      );
                      const visibleItems = JSON.parse(
                        listing.getAttribute("data-visible-items") || "[]",
                      );

                      if (isExpanded) {
                        listing.items = visibleItems;
                        listing.setAttribute("data-expanded", "false");
                        listing.requestUpdate?.();
                        button.textContent = seeAllLabel;
                      } else {
                        listing.items = allItems;
                        listing.setAttribute("data-expanded", "true");
                        listing.requestUpdate?.();
                        button.textContent = seeLessLabel;
                      }
                    }}
                    style="margin-top: 8px;"
                  >
                    ${seeAllLabel}
                  </apidae-button>
                `
              : ""}
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

type PaymentMethodItem = { id: number; enumKey: string; label: string };

export function renderPaymentMethodsSection(ctx: DetailViewContext) {
  if (
    !ctx.shouldDisplayField(DisplayedFieldId.MOYEN_PAIEMENT) ||
    !getFieldData(ctx, DisplayedFieldId.MOYEN_PAIEMENT)
  ) {
    return html``;
  }

  const paymentData = getFieldData(ctx, DisplayedFieldId.MOYEN_PAIEMENT);
  const paymentItems = (paymentData?.items || []) as PaymentMethodItem[];

  if (paymentItems.length === 0) {
    return html``;
  }

  const allPaymentMethods = paymentItems.map((pm) => ({
    icon: getModePaiementIconName({ id: pm.id, enumKey: pm.enumKey }),
    title: pm.label,
    content: "",
  }));

  const maxVisible = 12;
  const hasMoreThanMax = allPaymentMethods.length > maxVisible;
  const visiblePaymentMethods = hasMoreThanMax
    ? allPaymentMethods.slice(0, maxVisible)
    : allPaymentMethods;

  const paymentMethodsTitle =
    t("paymentMethods", ctx.language) ?? "Moyens de paiement";
  const seeAllLabel = `${t("seeAll", ctx.language) ?? "Voir tout"} (${allPaymentMethods.length})`;
  const seeLessLabel = t("showLess", ctx.language) ?? "Voir moins";

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          variant="grey"
          spacer="true"
          class="information-block--content-500"
        >
          <div slot="header">${paymentMethodsTitle}</div>
          <div slot="content">
            <apidae-detail-view-information-block-listing-w-columns
              class="payment-methods-listing"
              data-all-items="${JSON.stringify(allPaymentMethods)}"
              data-visible-items="${JSON.stringify(visiblePaymentMethods)}"
              .items=${visiblePaymentMethods}
            ></apidae-detail-view-information-block-listing-w-columns>
            ${hasMoreThanMax
              ? html`
                  <apidae-button
                    variant="secondary100"
                    @click=${(e: Event) => {
                      const button = e.currentTarget as HTMLElement;
                      const listing = button.previousElementSibling as any;

                      if (!listing) return;

                      const isExpanded =
                        listing.getAttribute("data-expanded") === "true";
                      const allItems = JSON.parse(
                        listing.getAttribute("data-all-items") || "[]",
                      );
                      const visibleItems = JSON.parse(
                        listing.getAttribute("data-visible-items") || "[]",
                      );

                      if (isExpanded) {
                        listing.items = visibleItems;
                        listing.setAttribute("data-expanded", "false");
                        listing.requestUpdate?.();
                        button.textContent = seeAllLabel;
                      } else {
                        listing.items = allItems;
                        listing.setAttribute("data-expanded", "true");
                        listing.requestUpdate?.();
                        button.textContent = seeLessLabel;
                      }
                    }}
                    style="margin-top: 8px;"
                  >
                    ${seeAllLabel}
                  </apidae-button>
                `
              : ""}
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

export function renderLabelsSection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.LABEL_DISTINCTION))
    return html``;
  const labelsData = getFieldData(ctx, DisplayedFieldId.LABEL_DISTINCTION);
  const distinctions = (labelsData?.items || []) as Array<{
    nom: string;
    qualification: string;
    categorie?: string | null;
    label: string;
  }>;

  if (distinctions.length === 0) {
    return html``;
  }

  const sectionTitle =
    t("labelsAndChaines", ctx.language) ?? "Labels & Chaînes";

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block variant="grey">
          <div
            slot="header"
            class="labels-section-header"
            style="width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; text-align: center; color: var(--widget-title-color, #2f2f2f);"
          >
            <apidae-icon
              name="bold/like/medal_ribbon_star"
              size="48"
              color="var(--widget-title-color, #2f2f2f)"
            ></apidae-icon>
            <span class="labels-section-title">${sectionTitle}</span>
          </div>
          <div slot="content" class="labels-section-content">
            <div
              style="display: flex; flex-wrap: wrap; gap: 16px; justify-content: center; align-items: stretch;"
            >
              ${distinctions.map((distinction) => {
                const label = distinction.label;
                return html`
                  <div
                    style="
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      gap: 8px;
                      min-width: 140px;
                      flex: 0 1 calc((100% - 32px) / 3);
                      box-sizing: border-box;
                    "
                  >
                    <div
                      class="labels-distinction-badge"
                      style="
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 16px;
                        background-color: color-mix(in srgb, var(--widget-primary-color, #168ac8) 10%, transparent);
                        border-radius: 20px;
                        width: 91px;
                        height: 91px;
                        box-sizing: border-box;
                      "
                    >
                      <svg
                        width="91"
                        height="91"
                        class="labels-distinction-badge-dashed"
                        style="position: absolute; top: 0; left: 0; pointer-events: none; border-radius: 20px;"
                        aria-hidden="true"
                      >
                        <rect
                          x="0.5"
                          y="0.5"
                          width="90"
                          height="90"
                          rx="20"
                          ry="20"
                          fill="none"
                        />
                      </svg>
                      <apidae-icon
                        name="bold/like/medal_ribbon_star"
                        size="32"
                        color="var(--widget-primary-color, #168ac8)"
                      ></apidae-icon>
                    </div>
                    <span
                      style="text-align: center; font-family: var(--widget-text-font-family, 'Inter', sans-serif); font-size: 16px; font-weight: 600; color: var(--widget-text-color, #2f2f2f); line-height: 150%"
                    >
                      ${label}
                    </span>
                  </div>
                `;
              })}
            </div>
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

export function renderAccessibilitySection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.ACCESSIBILITE)) {
    return html``;
  }

  const accessibiliteData = getFieldData(ctx, DisplayedFieldId.ACCESSIBILITE);
  if (!accessibiliteData) return html``;

  const accessibiliteItems = (accessibiliteData.items || []) as Array<{
    id: number;
    enumKey?: string;
    label: string;
  }>;
  const showBadge = !!accessibiliteData.showTourismAndDisabilityBadge;
  const descriptifs = accessibiliteData.descriptifs || {};
  const hasDescriptifs =
    !!descriptifs.moteur ||
    !!descriptifs.auditif ||
    !!descriptifs.visuel ||
    !!descriptifs.mental;

  if (accessibiliteItems.length === 0 && !showBadge && !hasDescriptifs) {
    return html``;
  }

  const allAccessibilities = accessibiliteItems.map((item) => ({
    icon: "bold/essentional_ui/accessibility",
    title: item.label,
    content: "",
  }));

  const maxVisible = 12;
  const hasMoreThanMax = allAccessibilities.length > maxVisible;
  const visibleAccessibilities = hasMoreThanMax
    ? allAccessibilities.slice(0, maxVisible)
    : allAccessibilities;
  const seeAllLabel = `${t("seeAll", ctx.language) ?? "Voir tout"} (${allAccessibilities.length})`;
  const seeLessLabel = t("showLess", ctx.language) ?? "Voir moins";

  const accessibilityTitle =
    t("accessibility", ctx.language) ?? "Accessibilité";
  const tourismAndDisabilityLabel =
    t("tourismAndDisability", ctx.language) ?? "Tourisme & Handicap";

  const handicapLabels = {
    moteur:
      t("descriptifHandicapMoteur", ctx.language) ??
      "Descriptif handicap moteur",
    auditif:
      t("descriptifHandicapAuditif", ctx.language) ??
      "Descriptif handicap auditif",
    visuel:
      t("descriptifHandicapVisuel", ctx.language) ??
      "Descriptif handicap visuel",
    mental:
      t("descriptifHandicapMental", ctx.language) ??
      "Descriptif handicap mental",
  };

  const descriptifBlocks = hasDescriptifs
    ? ([
        descriptifs.moteur && {
          title: handicapLabels.moteur,
          text: descriptifs.moteur,
        },
        descriptifs.auditif && {
          title: handicapLabels.auditif,
          text: descriptifs.auditif,
        },
        descriptifs.visuel && {
          title: handicapLabels.visuel,
          text: descriptifs.visuel,
        },
        descriptifs.mental && {
          title: handicapLabels.mental,
          text: descriptifs.mental,
        },
      ].filter(Boolean) as Array<{ title: string; text: string }>)
    : [];

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item" style="flex: 1;">
        <apidae-detail-view-information-block
          variant="secondary100"
          spacer="true"
          class="information-block--content-500"
        >
          <div
            slot="header"
            style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
          >
            <span>${accessibilityTitle}</span>
            ${showBadge
              ? html`
                  <div
                    class="accessibility-tourism-handicap-badge"
                    style="display: flex; align-items: center; gap: 8px; color: var(--widget-primary-color, #168ac8);"
                  >
                    <apidae-icon
                      name="bold/essentional_ui/accessibility"
                      size="20"
                      color="var(--widget-primary-color, #168ac8)"
                    ></apidae-icon>
                    <span style="font-weight: 500;"
                      >${tourismAndDisabilityLabel}</span
                    >
                  </div>
                `
              : ""}
          </div>
          ${accessibiliteItems.length > 0
            ? html`
                <div slot="content">
                  <apidae-detail-view-information-block-listing-w-columns
                    class="accessibility-listing"
                    data-all-items="${JSON.stringify(allAccessibilities)}"
                    data-visible-items="${JSON.stringify(
                      visibleAccessibilities,
                    )}"
                    .items=${visibleAccessibilities}
                  ></apidae-detail-view-information-block-listing-w-columns>
                  ${hasMoreThanMax
                    ? html`
                        <apidae-button
                          variant="secondary100"
                          @click=${(e: Event) => {
                            const button = e.currentTarget as HTMLElement;
                            const listing =
                              button.previousElementSibling as any;
                            if (!listing) return;
                            const isExpanded =
                              listing.getAttribute("data-expanded") === "true";
                            const allItems = JSON.parse(
                              listing.getAttribute("data-all-items") || "[]",
                            );
                            const visibleItems = JSON.parse(
                              listing.getAttribute("data-visible-items") ||
                                "[]",
                            );
                            if (isExpanded) {
                              listing.items = visibleItems;
                              listing.setAttribute("data-expanded", "false");
                              listing.requestUpdate?.();
                              button.textContent = seeAllLabel;
                            } else {
                              listing.items = allItems;
                              listing.setAttribute("data-expanded", "true");
                              listing.requestUpdate?.();
                              button.textContent = seeLessLabel;
                            }
                          }}
                          style="margin-top: 8px;"
                        >
                          ${seeAllLabel}
                        </apidae-button>
                      `
                    : ""}
                </div>
              `
            : ""}
        </apidae-detail-view-information-block>
      </div>
    </div>
    ${descriptifBlocks.length > 0
      ? (() => {
          const rows = chunk(descriptifBlocks, 2);
          return html`
            ${rows.map(
              (row) => html`
                <div class="detail-content-row">
                  ${row.map(
                    (d) => html`
                      <div class="detail-content-row-item">
                        <apidae-detail-view-information-block variant="grey">
                          <div slot="header">
                            <apidae-icon
                              name="bold/essentional_ui/accessibility"
                              size="20"
                              color="currentColor"
                            ></apidae-icon>
                            ${d.title}
                          </div>
                          <div slot="content">
                            <p>${d.text}</p>
                          </div>
                        </apidae-detail-view-information-block>
                      </div>
                    `,
                  )}
                </div>
              `,
            )}
          `;
        })()
      : ""}
  `;
}

export function renderRoomsAndAnimalsSection(ctx: DetailViewContext) {
  const locationSalleData = getFieldData(ctx, DisplayedFieldId.LOCATION_SALLE);
  const tourismeAffaires =
    (locationSalleData?.items && locationSalleData.items[0]) || {};

  const sallesModulables = tourismeAffaires.nombreSallesModulables ?? "";
  const sallesReunionEquipees =
    tourismeAffaires.nombreSallesReunionEquipees ?? "";
  const capaciteMaxAccueil = tourismeAffaires.capaciteMaxAccueil ?? "";

  const items = [
    sallesModulables && {
      icon: "bold/settings_fine_tuning/widget_5",
      title: t("modularRoomsCount", ctx.language),
      content: String(sallesModulables),
    },
    sallesReunionEquipees && {
      icon: "bold/business_statistic/presentation_graph",
      title: t("equippedMeetingRooms", ctx.language),
      content: String(sallesReunionEquipees),
    },
    capaciteMaxAccueil && {
      icon: "bold/users/users_group_two_rounded",
      title: t("maxCapacity", ctx.language),
      content: String(capaciteMaxAccueil),
    },
  ].filter(Boolean);

  const showRooms =
    ctx.shouldDisplayField(DisplayedFieldId.LOCATION_SALLE) && items.length > 0;

  const animauxData = getFieldData(ctx, DisplayedFieldId.ANIMAUX_ACCEPTES);
  const animauxAcceptes = animauxData?.rawValue as
    | "ACCEPTES"
    | "NON_ACCEPTES"
    | "NON_DISPONIBLE"
    | undefined;
  const showAnimals =
    ctx.shouldDisplayField(DisplayedFieldId.ANIMAUX_ACCEPTES) &&
    (animauxAcceptes === "ACCEPTES" || animauxAcceptes === "NON_ACCEPTES");
  const descriptifAnimauxAcceptes = animauxData?.descriptifAnimauxAcceptes;

  if (!showRooms && !showAnimals) return html``;

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        ${showRooms
          ? html`
              <apidae-detail-view-information-block
                variant="secondary100"
                spacer="true"
                class="information-block--content-500"
              >
                <div slot="header">${t("roomRental", ctx.language)}</div>
                <div slot="content">
                  <apidae-detail-view-information-block-listing
                    class="values-aligned listing-values-400"
                    .items=${items}
                  ></apidae-detail-view-information-block-listing>
                </div>
              </apidae-detail-view-information-block>
            `
          : ""}
      </div>
      <div class="detail-content-row-item">
        ${showAnimals
          ? html`
              <apidae-detail-view-information-block variant="grey">
                <div slot="header">
                  <apidae-icon
                    name="bold/essentional_ui/paw"
                    size="20"
                    color="currentColor"
                  ></apidae-icon>
                  ${t("animals", ctx.language)}
                </div>
                <div slot="content" class="animaux-content">
                  <p>
                    ${getAnimalWording(
                      animauxAcceptes as "ACCEPTES" | "NON_ACCEPTES",
                      animauxData?.animauxAcceptesSupplement,
                      (key) => t(key, ctx.language),
                    )}
                  </p>
                  ${descriptifAnimauxAcceptes
                    ? html`
                        <apidae-detail-view-information-block-disclaimer
                          text="${descriptifAnimauxAcceptes}"
                          logo="bold/essentional_ui/danger_triangle"
                          color="primary10"
                        ></apidae-detail-view-information-block-disclaimer>
                      `
                    : ""}
                </div>
              </apidae-detail-view-information-block>
            `
          : ""}
      </div>
    </div>
  `;
}

type CapabilityLine = {
  icon: string;
  label: string;
  value: number | string;
  unitKey: string;
};

function getCapabilityLines(
  capacites: Capacites,
  variant:
    | "hebergementCollectif"
    | "hebergementLocatif"
    | "hotelleriePleinAir"
    | "hotellerie",
  t: (key: string, language: WidgetLanguage) => string,
  language: WidgetLanguage,
): CapabilityLine[] {
  const lines: CapabilityLine[] = [];
  const add = (
    icon: string,
    labelKey: string,
    value: number | undefined | null,
    unitKey: string,
  ) => {
    if (value != null) {
      lines.push({
        icon,
        label: t(labelKey, language),
        value: String(value),
        unitKey,
      });
    }
  };

  if (variant === "hebergementCollectif") {
    add(
      "bold/building_infrastructure/home",
      "capacitesUnitesHebergementDeclarees",
      capacites.declarees?.unitesHebergement,
      "capacitesUnitHebergements",
    );
    add(
      "bold/users/users_group_two_rounded",
      "capacitesPersonnesDeclarees",
      capacites.declarees?.personnes,
      "capacitesUnitPersonnes",
    );
    const hco = capacites.hebergementCollectif!;
    add(
      "bold/home_furniture/hotel",
      "capacitesNombreChambres",
      hco.nombreChambres,
      "capacitesUnitChambres",
    );
    add(
      "bold/building_infrastructure/buildings_2",
      "capacitesNombreAppartements",
      hco.nombreAppartements,
      "capacitesUnitAppartements",
    );
    add(
      "bold/home_furniture/hotel",
      "capacitesNombreDortoirs",
      hco.nombreDortoirs,
      "capacitesUnitDortoirs",
    );
    add(
      "bold/building_infrastructure/home",
      "capacitesNombreVillas",
      hco.nombreVillas,
      "capacitesUnitVillas",
    );
  } else if (variant === "hebergementLocatif") {
    add(
      "bold/users/users_group_two_rounded",
      "capacitesPersonnesClassees",
      capacites.classees?.personnes,
      "capacitesUnitPersonnes",
    );
    const hlo = capacites.hebergementLocatif!;
    add(
      "bold/home_furniture/hotel",
      "capacitesNombreChambres",
      hlo.nombreChambres,
      "capacitesUnitChambres",
    );
    add(
      "bold/home_furniture/hotel",
      "capacitesNombreCabines",
      hlo.nombreCabines,
      "capacitesUnitCabines",
    );
    add(
      "bold/home_furniture/bed",
      "capacitesNombreLitsDoubles",
      hlo.nombreLitsDoubles,
      "capacitesUnitLits",
    );
    add(
      "bold/home_furniture/bed",
      "capacitesNombreLitsSimples",
      hlo.nombreLitsSimples,
      "capacitesUnitLits",
    );
    add(
      "bold/design_tools/ruler_angular",
      "capacitesSurface",
      hlo.surface,
      "capacitesUnitM2",
    );
    add(
      "bold/home_furniture/bath",
      "capacitesNombreSallesDeBain",
      hlo.nombreSallesDeBain,
      "capacitesUnitSallesDeBain",
    );
  } else if (variant === "hotelleriePleinAir") {
    add(
      "bold/map_&_location/point_on_map_perspective",
      "capacitesUnitesHebergementClassees",
      capacites.classees?.unitesHebergement,
      "capacitesUnitEmplacements",
    );
    add(
      "bold/design_tools/ruler_angular",
      "capacitesSuperficie",
      capacites.hotelleriePleinAir?.superficie,
      "capacitesUnitM2",
    );
    add(
      "bold/users/users_group_two_rounded",
      "capacitesPersonnesClassees",
      capacites.classees?.personnes,
      "capacitesUnitPersonnes",
    );
  } else {
    // hotellerie
    add(
      "bold/users/users_group_two_rounded",
      "capacitesPersonnesClassees",
      capacites.classees?.personnes,
      "capacitesUnitPersonnes",
    );
    add(
      "bold/home_furniture/hotel",
      "capacitesUnitesHebergementClassees",
      capacites.classees?.unitesHebergement,
      "capacitesUnitChambres",
    );
    const hot = capacites.hotellerie!;
    add(
      "bold/home_furniture/hotel",
      "capacitesNombreChambresSimples",
      hot.nombreChambresSimples,
      "capacitesUnitChambres",
    );
    add(
      "bold/home_furniture/hotel",
      "capacitesNombreChambresDoubles",
      hot.nombreChambresDoubles,
      "capacitesUnitChambres",
    );
    add(
      "bold/home_furniture/hotel",
      "capacitesNombreSuites",
      hot.nombreSuites,
      "capacitesUnitSuites",
    );
    add(
      "bold/home_furniture/hotel",
      "capacitesPmrUnitesHebergement",
      capacites.handicap?.pmrUnitesHebergement,
      "capacitesUnitChambres",
    );
  }
  return lines;
}

export function renderCapabilitiesSection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.CAPACITES)) return html``;
  const data = getFieldData(ctx, DisplayedFieldId.CAPACITES) as {
    capacites?: Capacites;
    capacitesVariant?:
      | "hebergementCollectif"
      | "hebergementLocatif"
      | "hotelleriePleinAir"
      | "hotellerie";
  } | null;
  if (!data?.capacites || !data.capacitesVariant) return html``;
  const lines = getCapabilityLines(
    data.capacites,
    data.capacitesVariant,
    t,
    ctx.language,
  );
  if (lines.length === 0) return html``;

  const items = lines.map((line) => ({
    icon: line.icon,
    title: line.label,
    content: `${line.value} ${t(line.unitKey, ctx.language)}`,
  }));

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          variant="secondary100"
          spacer="true"
          class="information-block--content-500"
        >
          <div slot="header">${t("capabilities", ctx.language)}</div>
          <div slot="content">
            <apidae-detail-view-information-block-listing
              class="values-aligned listing-values-400"
              .items=${items}
            ></apidae-detail-view-information-block-listing>
          </div>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

export function renderAlertAndTipsSection(ctx: DetailViewContext) {
  const items = getThematiquesForSection(
    ctx,
    DESCRIPTIF_THEME_IDS_BY_SECTION.ALERTE_ET_CONSEILS,
  );
  if (items.length === 0) return html``;

  const rows = chunk(items, 2);
  return html`
    ${rows.map(
      (row, rowIndex) => html`
        <div class="detail-content-row">
          ${row.map(
            (item, colIndex) => html`
              <div class="detail-content-row-item">
                ${renderThematiqueBlock(item, rowIndex * 2 + colIndex)}
              </div>
            `,
          )}
        </div>
      `,
    )}
  `;
}

type TopoItem = {
  theme: { id: number; enumKey?: string; label: string; ordre?: number };
  description: string;
};

function getThematiquesForSection(
  ctx: DetailViewContext,
  themeIds: readonly number[],
): TopoItem[] {
  if (!ctx.shouldDisplayField(DisplayedFieldId.DESCRIPTIFS_THEMATISES))
    return [];
  const data = getFieldData(ctx, DisplayedFieldId.DESCRIPTIFS_THEMATISES);
  const items = (data?.items ?? []) as TopoItem[];
  const idSet = new Set(themeIds);
  return items.filter(
    (item) =>
      item.theme &&
      idSet.has(item.theme.id) &&
      (item.description?.trim() ?? "") !== "",
  );
}

function getOtherThematiques(ctx: DetailViewContext): TopoItem[] {
  if (!ctx.shouldDisplayField(DisplayedFieldId.DESCRIPTIFS_THEMATISES))
    return [];
  const data = getFieldData(ctx, DisplayedFieldId.DESCRIPTIFS_THEMATISES);
  const items = (data?.items ?? []) as TopoItem[];
  return items
    .filter(
      (item) =>
        item.theme &&
        isOtherDescriptifTheme(item.theme.id) &&
        (item.description?.trim() ?? "") !== "",
    )
    .sort(
      (a, b) => (a.theme.ordre ?? a.theme.id) - (b.theme.ordre ?? b.theme.id),
    );
}

/** Découpe un tableau en sous-tableaux de taille max size */
function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

function renderThematiqueBlock(item: TopoItem, index: number) {
  return html`
    <apidae-detail-view-information-block
      variant="${getDescriptifThemeVariant(item.theme, index)}"
    >
      <div slot="header">
        <apidae-icon
          name="${getDescriptifThemeIconName(item.theme)}"
          size="20"
          color="currentColor"
        ></apidae-icon>
        ${item.theme.label}
      </div>
      <div slot="content">
        <p>${item.description}</p>
      </div>
    </apidae-detail-view-information-block>
  `;
}

export function renderItineraryAndTopoSection(ctx: DetailViewContext) {
  const showItinerary = ctx.shouldDisplayField(
    DisplayedFieldId.INFOS_ITINERAIRES,
  );
  const showTopo = ctx.shouldDisplayField(
    DisplayedFieldId.DESCRIPTIFS_THEMATISES,
  );

  const itineraryData = showItinerary
    ? getFieldData(ctx, DisplayedFieldId.INFOS_ITINERAIRES)
    : null;
  const topoData = showTopo
    ? getFieldData(ctx, DisplayedFieldId.DESCRIPTIFS_THEMATISES)
    : null;

  const itineraryRows = itineraryData?.itineraryRows ?? [];
  const precisionsBalisage = itineraryData?.precisionsBalisage;
  const allThematiques = (topoData?.items ?? []) as TopoItem[];
  const topoPasAPasItem = allThematiques.find((item) =>
    isTopoPasAPasTheme(item.theme),
  );
  const hasTopo =
    !!topoPasAPasItem && (topoPasAPasItem.description?.trim() ?? "") !== "";

  const hasItinerary = itineraryRows.length > 0 || !!precisionsBalisage;

  if (!hasItinerary && !hasTopo) {
    return html``;
  }

  const itineraryTitle = t("itinerary", ctx.language) ?? "Itinéraire";

  const itineraryListingItems = itineraryRows.map((row) => ({
    icon: row.icon,
    title: row.label,
    content: row.unit ? `${row.value} ${row.unit}`.trim() : row.value,
  }));

  return html`
    <div class="detail-content-row">
      ${hasItinerary
        ? html`
            <div class="detail-content-row-item">
              <apidae-detail-view-information-block
                variant="secondary100"
                spacer="true"
                class="information-block--content-500"
              >
                <div slot="header">${itineraryTitle}</div>
                <div slot="content">
                  ${itineraryListingItems.length > 0
                    ? html`
                        <apidae-detail-view-information-block-listing
                          class="values-aligned listing-values-400"
                          .items=${itineraryListingItems}
                        ></apidae-detail-view-information-block-listing>
                      `
                    : ""}
                  ${precisionsBalisage
                    ? html`
                        <apidae-detail-view-information-block-disclaimer
                          color="primary10"
                          logo="bold/map_&_location/signpost"
                          .text=${`${t("balisage", ctx.language) ?? "Balisage"} : ${precisionsBalisage}`}
                        ></apidae-detail-view-information-block-disclaimer>
                      `
                    : ""}
                </div>
              </apidae-detail-view-information-block>
            </div>
          `
        : ""}
      ${hasTopo && topoPasAPasItem
        ? html`
            <div class="detail-content-row-item">
              <apidae-detail-view-information-block
                variant="${getDescriptifThemeVariant(topoPasAPasItem.theme)}"
              >
                <div slot="header">
                  <apidae-icon
                    name="${getDescriptifThemeIconName(topoPasAPasItem.theme)}"
                    size="20"
                    color="currentColor"
                  ></apidae-icon>
                  ${topoPasAPasItem.theme.label}
                </div>
                <div slot="content">
                  <p>${topoPasAPasItem.description}</p>
                </div>
              </apidae-detail-view-information-block>
            </div>
          `
        : ""}
    </div>
  `;
}

export function renderSustainableAndKidsSection(ctx: DetailViewContext) {
  const items = getThematiquesForSection(
    ctx,
    DESCRIPTIF_THEME_IDS_BY_SECTION.SUSTAINABLE_AND_KIDS,
  );
  if (items.length === 0) return html``;

  const rows = chunk(items, 2);
  return html`
    ${rows.map(
      (row, rowIndex) => html`
        <div class="detail-content-row">
          ${row.map(
            (item, colIndex) => html`
              <div class="detail-content-row-item">
                ${renderThematiqueBlock(item, rowIndex * 2 + colIndex)}
              </div>
            `,
          )}
        </div>
      `,
    )}
  `;
}

export function renderNewsAndOffersSection(ctx: DetailViewContext) {
  const items = getThematiquesForSection(
    ctx,
    DESCRIPTIF_THEME_IDS_BY_SECTION.NEWS_AND_OFFERS,
  );
  if (items.length === 0) return html``;

  const rows = chunk(items, 2);
  return html`
    ${rows.map(
      (row, rowIndex) => html`
        <div class="detail-content-row">
          ${row.map(
            (item, colIndex) => html`
              <div class="detail-content-row-item">
                ${renderThematiqueBlock(item, rowIndex * 2 + colIndex)}
              </div>
            `,
          )}
        </div>
      `,
    )}
  `;
}

export function renderCyclistsAndPublicSection(ctx: DetailViewContext) {
  const cyclistes = getThematiquesForSection(
    ctx,
    DESCRIPTIF_THEME_IDS_BY_SECTION.CYCLISTS_AND_PUBLIC,
  );
  const showPublic = ctx.shouldDisplayField(DisplayedFieldId.PUBLIC_NIVEAU);
  const publicData = showPublic
    ? getFieldData(ctx, DisplayedFieldId.PUBLIC_NIVEAU)
    : null;
  const publicItems = (publicData?.items ?? []) as Array<{
    id: number;
    enumKey?: string;
    label: string;
  }>;
  const hasPublic = publicItems.length > 0;
  const hasContent = cyclistes.length > 0 || hasPublic;
  if (!hasContent) return html``;

  const allPublicItems = publicItems.map((item) => ({
    icon: getTypeClienteleIconName({ id: item.id, enumKey: item.enumKey }),
    title: item.label,
    content: "",
  }));
  const publicTitle = t("audience", ctx.language) ?? "Public";
  const maxVisiblePublic = 12;
  const hasMoreThanMaxPublic = allPublicItems.length > maxVisiblePublic;
  const visiblePublicItems = hasMoreThanMaxPublic
    ? allPublicItems.slice(0, maxVisiblePublic)
    : allPublicItems;
  const seeAllLabelPublic = `${t("seeAll", ctx.language) ?? "Voir tout"} (${allPublicItems.length})`;
  const seeLessLabelPublic = t("showLess", ctx.language) ?? "Voir moins";

  const publicBlock = hasPublic
    ? html`
        <div class="detail-content-row-item">
          <apidae-detail-view-information-block
            variant="grey"
            spacer="true"
            class="information-block--content-500"
          >
            <div slot="header">${publicTitle}</div>
            <div slot="content">
              <apidae-detail-view-information-block-listing-w-columns
                class="public-listing"
                .columns=${1}
                data-all-items="${JSON.stringify(allPublicItems)}"
                data-visible-items="${JSON.stringify(visiblePublicItems)}"
                .items=${visiblePublicItems}
              ></apidae-detail-view-information-block-listing-w-columns>
              ${hasMoreThanMaxPublic
                ? html`
                    <apidae-button
                      variant="secondary100"
                      @click=${(e: Event) => {
                        const button = e.currentTarget as HTMLElement;
                        const listing = button.previousElementSibling as any;
                        if (!listing) return;
                        const isExpanded =
                          listing.getAttribute("data-expanded") === "true";
                        const allItems = JSON.parse(
                          listing.getAttribute("data-all-items") || "[]",
                        );
                        const visibleItems = JSON.parse(
                          listing.getAttribute("data-visible-items") || "[]",
                        );
                        if (isExpanded) {
                          listing.items = visibleItems;
                          listing.setAttribute("data-expanded", "false");
                          listing.requestUpdate?.();
                          button.textContent = seeAllLabelPublic;
                        } else {
                          listing.items = allItems;
                          listing.setAttribute("data-expanded", "true");
                          listing.requestUpdate?.();
                          button.textContent = seeLessLabelPublic;
                        }
                      }}
                      style="margin-top: 8px;"
                    >
                      ${seeAllLabelPublic}
                    </apidae-button>
                  `
                : ""}
            </div>
          </apidae-detail-view-information-block>
        </div>
      `
    : null;

  const cells: ReturnType<typeof html>[] = [
    ...cyclistes.map(
      (item, i) => html`
        <div class="detail-content-row-item">
          ${renderThematiqueBlock(item, i)}
        </div>
      `,
    ),
    ...(publicBlock ? [publicBlock] : []),
  ];
  const rows = chunk(cells, 2);

  return html`
    ${rows.map((row) => html` <div class="detail-content-row">${row}</div> `)}
  `;
}

export function renderOtherDescriptifThematiseSection(ctx: DetailViewContext) {
  const items = getOtherThematiques(ctx);
  if (items.length === 0) return html``;

  const rows = chunk(items, 2);
  return html`
    ${rows.map(
      (row, rowIndex) => html`
        <div class="detail-content-row">
          ${row.map(
            (item, colIndex) => html`
              <div class="detail-content-row-item">
                ${renderThematiqueBlock(item, rowIndex * 2 + colIndex)}
              </div>
            `,
          )}
        </div>
      `,
    )}
  `;
}

/**
 * Section Carte : intègre le MapWidget avec la localisation de l'objet.
 * Affichée uniquement si l'objet a des coordonnées GPS.
 * Le bouton "Voir sur la carte" de la section localisation scrolle jusqu'ici avec animation.
 * MapWidget est chargé à la demande (ctx.mapWidgetLoaded) pour éviter une dépendance circulaire.
 */
export function renderMapSection(ctx: DetailViewContext) {
  const hasCoords =
    !!ctx.item?.localisation?.geolocalisation?.geoJson?.coordinates;
  if (!hasCoords) return html``;

  return html`
    <div id="detail-map-section">
      ${ctx.mapWidgetLoaded
        ? html`
            <apidae-widget-carte
              ischild
              .items=${[ctx.item]}
              .config=${ctx.widgetConfig ?? null}
              .hideHeaderAndFilters=${true}
              .hideEmptyState=${true}
              .isChildWidget=${true}
            ></apidae-widget-carte>
          `
        : html`<div class="detail-map-loading" aria-busy="true"></div>`}
    </div>
  `;
}

/**
 * Section Réservation en ligne (Widget Direct OpenSystem / Alliance Réseaux).
 * Affichée si DISPONIBILITES_OPENSYSTEM est configuré (idPanier + idIntegration dans la config)
 * et que l'objet a des métadonnées open-system (ui).
 *
 * Le conteneur est monté dans le document (body ou #apidae-open-system-portal) via apidae-open-system-widget.
 */
export function renderOpenSystemSection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.DISPONIBILITES_OPENSYSTEM))
    return html``;
  const data = getFieldData(ctx, DisplayedFieldId.DISPONIBILITES_OPENSYSTEM);
  const widget = data?.openSystemWidget;
  if (!widget) return html``;

  const { ui, integration } = widget;
  const langue =
    ctx.language === WidgetLanguage.EN ? "uk" : (ctx.language as string);
  const sectionTitle =
    t("onlineBooking", ctx.language) || "Réservation en ligne";

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          class="open-system-section"
          variant="secondary100"
          ?spacer=${false}
        >
          <div slot="header">${sectionTitle}</div>
          <apidae-open-system-widget
            slot="content"
            .ui=${ui}
            .idPanier=${integration.idPanier}
            .idIntegration=${integration.id}
            .langue=${langue}
            .sectionTitle=${sectionTitle}
          ></apidae-open-system-widget>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}

/**
 * Section Réservation en ligne (Widget Elloha).
 * Affichée si DISPONIBILITES_ELLOHA est configuré (idBookingEngine dans la config)
 * et que l'objet a des métadonnées elloha (id produit).
 */
export function renderEllohaSection(ctx: DetailViewContext) {
  if (!ctx.shouldDisplayField(DisplayedFieldId.DISPONIBILITES_ELLOHA))
    return html``;
  const data = getFieldData(ctx, DisplayedFieldId.DISPONIBILITES_ELLOHA);
  const elloha = data?.ellohaWidget;
  if (!elloha?.idProduct || !elloha?.idBookingEngine) return html``;

  const sectionTitle =
    t("onlineBooking", ctx.language) || "Réservation en ligne";
  const culture =
    ctx.language === WidgetLanguage.EN
      ? "en-GB"
      : ctx.language === WidgetLanguage.ES
        ? "es-ES"
        : ctx.language === WidgetLanguage.DE
          ? "de-DE"
          : ctx.language === WidgetLanguage.IT
            ? "it-IT"
            : "fr-FR";

  return html`
    <div class="detail-content-row">
      <div class="detail-content-row-item">
        <apidae-detail-view-information-block
          class="elloha-section"
          variant="secondary100"
          ?spacer=${false}
        >
          <div slot="header">${sectionTitle}</div>
          <apidae-elloha-widget
            slot="content"
            .idBookingEngine=${elloha.idBookingEngine}
            .idProduct=${elloha.idProduct}
            .culture=${culture}
            .sectionTitle=${sectionTitle}
          ></apidae-elloha-widget>
        </apidae-detail-view-information-block>
      </div>
    </div>
  `;
}
